import React, { memo, useCallback, useEffect, useMemo, useReducer } from 'react';
import { useParams } from 'react-router-dom';
import { gql } from '@apollo/client';
import loadable from '@loadable/component';
import { Availability, FulfillmentTypes, compact, useFeatureFlag } from '@moda/portal-stanchions';
import { BadgeTheme, Stack, Text } from '@moda/om';
import { colors } from '@moda/tokens';
import AlertIcon from '@moda/icons/warning-16';
import { hrefFor } from '../../routers/PageRoutes';
import { useAddToBag } from '../../hooks/useAddToBag';
import { useCollectionBadges } from '../../hooks/useCollectionBadges';
import { useUrlParams } from '../../hooks/useUrlParams';
import { useUser } from '../../hooks/useUser';
import { UserStatusLevel } from '../../constants';
import { getActiveVariantBy } from '../../lib/getActiveVariantBy';
import { tc } from '../../lib/trackingContext';
import {
  ProductDetailsFragment,
  ProductDetailsVariantFragment,
  SizeInventoryFragment
} from '../../generated/types';
import { ADD_TO_BAG_FRAGMENT } from '../AddToBagButton';
import { isSpectacularGift } from '../../lib/isSpectacularGift';
import { ADMIN_MODE_PRODUCT_INFO_FRAGMENT } from '../AdminMode/AdminModeProductInfo';
import { ADMIN_PRODUCT_CONTROLS_FRAGMENT } from '../AdminMode/AdminProductControls/AdminProductControlsFragment';
import { CollectionBadge } from '../CollectionBadge';
import { ColorSelector, COLOR_SELECTOR_FRAGMENT } from '../ColorSelector';
import { FAVORITE_FRAGMENT } from '../../components/Favorite';
import { Link } from '../Link';
import { PdpInfoContainer, PDP_INFO_MODULE_FRAGMENT } from '../PdpInfoContainer';
import { PDPProductPrice } from '../PDPProductPrice';
import {
  PRODUCT_CUSTOMIZATION_FRAGMENT,
  ProductCustomization,
  useProductCustomization
} from '../ProductCustomization';
import {
  PRODUCT_SHARE_FRAGMENT,
  PRODUCT_SHARE_VARIANT_FRAGMENT,
  ProductShare
} from '../ProductShare';
import {
  NotifyMeButton,
  SIZE_SELECTOR_FRAGMENT,
  VARIANT_INVENTORY_FRAGMENT
} from '../SizeSelector';
import { trackProductViewed } from '../../hooks/useTrackProductViewed';
import { useAdminMode } from '../AdminMode/AdminModeProvider';
import { ProductShareV2Mobile } from '../ProductShareV2';
import { ProductDetailsCallToActionSection } from './ProductDetailsCallToActionSection';
import { ProductDeliveryOptions } from './ProductDeliveryOptions';
import { ProductDetailsSize } from './ProductDetailsSize';
import { ProductDetailsSoldOutLabel } from './ProductDetailsSoldOutLabel';
import { ProductDetailsTagsSection, PDP_BADGES_FRAGMENT } from './ProductDetailsTagsSection';
import {
  PRODUCT_DETAILS_EXPANDED_FRAGMENT,
  PRODUCT_DETAILS_EXPANDED_VARIANT_FRAGMENT,
  ProductDetailsExpanded
} from './ProductDetailsExpanded';
import { ProductDetailsQuantitySelector } from './ProductDetailsQuantitySelector';
import { EditorsNote } from './EditorsNote';
import {
  COLOR_SWITCHER_DESCRIPTION_VARIANT_FRAGMENT,
  ColorSwitcherDescription
} from './ColorSwitcherDescription';

import './ProductDetails.scss';

const PRODUCT_DETAILS_VARIANT_FRAGMENT = gql`
  fragment ProductDetailsVariantFragment on VariantData {
    id
    color
    collections
    href
    soldOut: sold_out
    returnPolicy: return_policy
    inventory {
      __typename
    }
    prices(country_code: $countryCode) {
      originalPrice: original_price {
        currency
        price
      }
      currentPrice: current_price {
        currency
        price
      }
      depositPrice: deposit_price {
        currency
        price
      }
      vat {
        currency
        price
      }
    }
    ...PDPBadgesFragment
    ...ProductShareVariantFragment
    ...ColorSwitcherDescriptionVariantFragment
    ...ProductDetailsExpandedVariantFragment
    ...VariantInventoryFragment
  }

  ${PRODUCT_SHARE_VARIANT_FRAGMENT}
  ${PDP_BADGES_FRAGMENT}
  ${COLOR_SWITCHER_DESCRIPTION_VARIANT_FRAGMENT}
  ${PRODUCT_DETAILS_EXPANDED_VARIANT_FRAGMENT}
  ${VARIANT_INVENTORY_FRAGMENT}
`;

export const PRODUCT_DETAILS_FRAGMENT = gql`
  fragment ProductDetailsFragment on Variant {
    name
    tag
    designerName: designer_name
    designerSlug: designer_slug
    available
    availability
    fulfilmentType: fulfilment_type
    category
    customizable
    objectid
    orderLimit: order_limit
    index
    customizationParameters: customization_parameters {
      ...ProductCustomizationFragment
    }
    sizeEquivalents: size_equivalents
    fitRecommendation: fit_recommendation {
      description
    }
    masterVariant: master_variants_data {
      ...ProductDetailsVariantFragment
    }
    otherVariants: other_variants_data {
      ...ProductDetailsVariantFragment
    }
    shopAttributes: shop_attributes
    subcategory
    ...ColorSelectorFragment
    ...SizeSelectorFragment
    ...AddToBagFragment
    ...FavoriteFragment
    ...ProductDetailsExpandedFragment
    ...PdpInfoContainerFragment
    ...ProductShareFragment
    ...AdminProductControlsFragment
    ...AdminModeProductInfoFragment
  }
  ${COLOR_SELECTOR_FRAGMENT}
  ${SIZE_SELECTOR_FRAGMENT}
  ${ADD_TO_BAG_FRAGMENT}
  ${FAVORITE_FRAGMENT}
  ${PRODUCT_DETAILS_VARIANT_FRAGMENT}
  ${PRODUCT_DETAILS_EXPANDED_FRAGMENT}
  ${PRODUCT_SHARE_FRAGMENT}
  ${PDP_INFO_MODULE_FRAGMENT}
  ${PRODUCT_CUSTOMIZATION_FRAGMENT}
  ${ADMIN_PRODUCT_CONTROLS_FRAGMENT}
  ${ADMIN_MODE_PRODUCT_INFO_FRAGMENT}
`;

interface Props {
  variant: ProductDetailsFragment;
}

interface State {
  size: SizeInventoryFragment | null;
  hoveredColor: string | undefined;
  quantity: number;
}

type Action =
  | { type: 'SELECT_SIZE'; payload: { size: SizeInventoryFragment | null } }
  | { type: 'CLEAR_COLOR_HOVER' }
  | { type: 'HOVER_COLOR'; payload: { color: string } }
  | { type: 'SELECT_QUANTITY'; payload: { quantity: number } };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SELECT_SIZE':
      return { ...state, size: action.payload.size };
    case 'HOVER_COLOR':
      return { ...state, hoveredColor: action.payload.color };
    case 'CLEAR_COLOR_HOVER':
      return { ...state, hoveredColor: undefined };
    case 'SELECT_QUANTITY':
      return { ...state, quantity: action.payload.quantity };
  }
};

const AdminProductControls = loadable(() => import('../AdminMode/AdminProductControls'), {
  resolveComponent: exports => exports.AdminProductControls
});

const AdminModeProductInfo = loadable(() => import('../AdminMode/AdminModeProductInfo'), {
  resolveComponent: exports => exports.AdminModeProductInfo
});

export const ProductDetails: React.FC<Props> = memo(({ variant }) => {
  const {
    customizationMessage,
    customizationMessageError,
    handleSetCustomizationMessage,
    isCustomizationValid
  } = useProductCustomization(variant.customizationParameters);
  const { vertical = 'women' } = useParams<{ vertical?: string }>();
  const { params, setParams } = useUrlParams();
  const activeVariant = useMemo(() => getActiveVariantBy(variant, 'id', variant.id), [variant]);
  const priceBadge = useCollectionBadges(activeVariant.collections);
  const { enabled: adminModeEnabled } = useAdminMode();

  const { user } = useUser();
  const consignmentAlert =
    user.affiliate === 1 &&
    (variant.fulfilmentType === FulfillmentTypes.DropShip ||
      variant.fulfilmentType === FulfillmentTypes.MiraklEconcession ||
      variant.fulfilmentType === FulfillmentTypes.MiraklDropship);

  const { isOneSizedItem } = useAddToBag({
    activeVariantId: activeVariant.id,
    variant,
    validatesSizePresenceOn: 'always'
  });

  const defaultSize = useMemo(() => {
    if (compact(activeVariant.inventory).length === 1) {
      return activeVariant.inventory[0];
    }

    if (params.size) {
      const matchedSize = activeVariant.inventory.find(
        inventoryItem => inventoryItem?.size === params.size
      );

      if (matchedSize) {
        return matchedSize;
      }
    }
    return null;
  }, [activeVariant.inventory, params]);

  const [state, dispatch] = useReducer(reducer, {
    size: defaultSize,
    hoveredColor: undefined,
    quantity: 1
  });

  // reset size selection when changing variant (color)
  useEffect(() => {
    dispatch({ type: 'SELECT_SIZE', payload: { size: defaultSize } });
  }, [defaultSize, activeVariant]);

  const handleSelectSize = useCallback((option: SizeInventoryFragment) => {
    if (option.size)
      dispatch({
        type: 'SELECT_SIZE',
        payload: { size: { ...option, available: option.available || 0 } }
      });
  }, []);

  const handleSelectColor = useCallback(
    (color: string) => {
      const variantForColor = getActiveVariantBy(
        variant,
        variant => variant.customColorName || variant.color,
        color
      );

      if (activeVariant.id !== variantForColor.id) {
        setParams({ ...params, variantId: variantForColor?.id });
      }
    },
    [activeVariant, variant, params, setParams]
  );

  const handleColorMouseEnter = useCallback((color: string) => {
    dispatch({ type: 'HOVER_COLOR', payload: { color } });
  }, []);

  const clearColorHover = useCallback(() => {
    dispatch({ type: 'CLEAR_COLOR_HOVER' });
  }, []);

  const handleSelectQuantity = useCallback(
    (quantity: number) => {
      dispatch({ type: 'SELECT_QUANTITY', payload: { quantity } });
    },
    [dispatch]
  );

  const colorDetailsVariant = state.hoveredColor
    ? getActiveVariantBy<ProductDetailsVariantFragment>(variant, 'color', state.hoveredColor)
    : activeVariant;

  useEffect(() => {
    trackProductViewed({
      variant,
      activeVariant,
      selectedSize: state.size,
      badgeMessage: priceBadge?.message || null,
      user
    });

    return () => tc.setCurrent('variant', undefined);
    // Explicitly run only once, on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVariant.id]);

  const isTrunkshow = variant.availability === String(Availability.Trunkshow);

  const deliveryOptionsEnabled = useFeatureFlag('pdp-delivery-options', [
    { default: false },
    { if: true, then: true }
  ]);

  const showDeliveryOptions =
    deliveryOptionsEnabled &&
    variant.available &&
    variant.availability === String(Availability.AvailableNow) &&
    user.statusLevel !== UserStatusLevel.VIP &&
    !isSpectacularGift(variant);

  return (
    <div className="ProductDetails">
      {adminModeEnabled && (
        <Stack className="ProductDetails__admin-mode-product-controls" space={2}>
          <AdminProductControls variant={variant} activeVariantId={activeVariant.id} />

          <AdminModeProductInfo variant={variant} activeVariantId={activeVariant.id} />
        </Stack>
      )}

      <ProductDetailsTagsSection tag={variant.tag} badges={activeVariant.badges as BadgeTheme[]} />

      {isTrunkshow && !isSpectacularGift(variant) && (
        <Text
          className="ProductDetails__trunkshow-eyebrow"
          treatment="eyebrow"
          color="burnt-orange"
        >
          TRUNKSHOW
        </Text>
      )}

      {variant.designerSlug && (
        <Stack direction="horizontal" justifyContent="space-between" space={2}>
          <Link
            className="ProductDetails__designer"
            to={hrefFor.DesignerPage({ designerSlug: variant.designerSlug, vertical: vertical })}
          >
            {variant.designerName}
          </Link>

          <ProductShareV2Mobile />
        </Stack>
      )}

      <h1 className="ProductDetails__name">{variant.name}</h1>

      {activeVariant.prices && (
        <PDPProductPrice
          className="ProductDetails__price"
          payInFull={activeVariant.payInFull}
          depositPrice={activeVariant.prices.depositPrice}
          originalPrice={activeVariant.prices.originalPrice}
          currentPrice={activeVariant.prices.currentPrice}
          vatPrice={activeVariant.prices.vat}
        >
          <CollectionBadge collections={activeVariant.collections} />
        </PDPProductPrice>
      )}

      <Stack space={5}>
        {!isSpectacularGift(variant) && (
          <>
            <Stack space={2}>
              <ColorSwitcherDescription colorDetailsVariant={colorDetailsVariant} />

              {variant.otherVariants.length > 0 && (
                <ColorSelector
                  layout="grid"
                  variant={variant}
                  onMouseEnter={handleColorMouseEnter}
                  onMouseLeave={() => clearColorHover()}
                  onSelect={handleSelectColor}
                  selected={activeVariant.customColorName || activeVariant.color}
                />
              )}
            </Stack>

            <ProductDetailsSize
              activeVariant={activeVariant}
              variant={variant}
              handleSelectSize={handleSelectSize}
              size={state.size}
            />

            {vertical === 'home' && variant.available && (
              <ProductDetailsQuantitySelector
                quantity={state.quantity}
                quantityAvailable={
                  variant.availability === Availability.AvailableNow.toString() ||
                  variant.availability === Availability.Preorder.toString()
                    ? (state.size?.available ?? undefined)
                    : undefined
                }
                onChange={handleSelectQuantity}
                orderLimit={variant.orderLimit}
              />
            )}
          </>
        )}

        <ProductDetailsSoldOutLabel available={variant.available} />

        {variant.customizable && variant.customizationParameters && (
          <ProductCustomization
            customizationMessage={customizationMessage}
            customizationParameters={variant.customizationParameters}
            customizationMessageError={customizationMessageError}
            onChange={handleSetCustomizationMessage}
          />
        )}

        {!isSpectacularGift(variant) && (
          <PdpInfoContainer variant={variant} selectedSize={state.size} />
        )}

        {consignmentAlert && (
          <Stack space={2} direction="horizontal" alignItems="center">
            <AlertIcon color={colors.all['code-red']} />
            <Text color="code-red">Ineligible for consignment orders</Text>
          </Stack>
        )}

        {isOneSizedItem && !variant.available && (
          <NotifyMeButton activeVariantId={activeVariant.id} variant={variant} />
        )}

        <ProductDetailsCallToActionSection
          variant={variant}
          activeVariant={activeVariant}
          size={state.size}
          quantity={state.quantity}
          selectedSize={state.size}
          customizationMessage={customizationMessage}
          isCustomizationValid={isCustomizationValid}
          isSpectacularGift={isSpectacularGift(variant)}
        />

        {showDeliveryOptions && (
          <ProductDeliveryOptions
            variantId={activeVariant.id}
            fulfilmentType={variant.fulfilmentType}
            size={state.size}
          />
        )}
      </Stack>

      <EditorsNote variant={variant} />

      <ProductDetailsExpanded activeVariant={activeVariant} variant={variant} />

      <ProductShare activeVariant={activeVariant} variant={variant} />
    </div>
  );
});
